import React, { useState, useRef } from 'react';
import { 
  Col, 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  Row, 
  Card, 
  CardImg, 
  CardBody,
  Badge,
  Spinner,
  Alert,
  Progress
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../store/actions';
import { toast } from 'react-toastify';

function BannerUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedBanners, setUploadedBanners] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  
  const { bannerLinks, loading } = useSelector(state => state.settings);
  const dispatch = useDispatch();

  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/jpg"];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile) {
      toast.info('يرجى تحديد ملف أولاً!');
      return;
    }

    setUploading(true);
    try {
      dispatch(actions.uploadBannerRequest(selectedFile));
      
      // Reset form
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Refresh banners from server after upload
      setTimeout(() => {
        dispatch(actions.fetchDataRequest());
        setUploading(false);
      }, 1000);
      
      toast.success("تم رفع البانر بنجاح!", {
        icon: "✅",
        position: "top-center"
      });
    } catch (error) {
      toast.error("فشل رفع البانر");
      setUploading(false);
    }
  };

  const isFileValid = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("نوع الملف غير صالح. يرجى تحميل صورة (JPG, PNG, GIF).");
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`حجم الملف كبير جداً. الحد الأقصى ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)} ميجابايت.`);
      return false;
    }
    return true;
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file && isFileValid(file)) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      toast.success("تم اختيار الصورة بنجاح!");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && isFileValid(file)) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      toast.success("تم اختيار الصورة بنجاح!");
    }
  };

  const handleDeleteImage = (index, file) => {
    // Ensure we have the key property
    if (!file || !file.key) {
      toast.error('خطأ: معلومات البانر غير صحيحة');
      return;
    }

    const fileData = {
      keyName: file.key,
    };
    
    dispatch(actions.deleteDataRequest(fileData));
    toast.info("جاري حذف البانر...");
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const allBanners = bannerLinks || [];

  return (
    <Row className="mb-4">
      <Col xl={12}>
        <div className="d-flex align-items-center mb-3">
          <div className="avatar-sm me-3">
            <span className="avatar-title rounded-circle bg-info-subtle text-info font-size-20">
              <i className="bx bx-image"></i>
            </span>
          </div>
          <div>
            <h5 className="mb-1">إدارة البانرات</h5>
            <p className="text-muted mb-0">رفع وإدارة صور البانرات الإعلانية</p>
          </div>
        </div>

        <Card className="border shadow-sm" style={{ borderRadius: '12px' }}>
          <CardBody className="p-4">
            <Row>
              {/* Upload Section */}
              <Col lg={5} className="border-end">
                <h6 className="mb-3">
                  <i className="bx bx-upload me-2"></i>
                  رفع بانر جديد
                </h6>
                
                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <div
                      className={`upload-area p-4 text-center rounded ${isDragging ? 'dragging' : ''}`}
                      style={{
                        border: isDragging ? '2px dashed #556ee6' : '2px dashed #ced4da',
                        backgroundColor: isDragging ? '#f8f9fa' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i className="bx bx-cloud-upload display-4 text-muted mb-3 d-block"></i>
                      <p className="mb-2">
                        <strong>اسحب وأفلت الصورة هنا</strong>
                      </p>
                      <p className="text-muted mb-2">أو</p>
                      <Button color="primary" size="sm" outline>
                        <i className="bx bx-folder-open me-1"></i>
                        اختر ملف
                      </Button>
                      <Input
                        innerRef={fileInputRef}
                        type="file"
                        name="file"
                        id="bannerUpload"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                    
                    <div className="mt-2 d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        <i className="bx bx-info-circle me-1"></i>
                        JPG, PNG, GIF - حد أقصى 1MB
                      </small>
                      {selectedFile && (
                        <Badge color="success">
                          <i className="bx bx-check me-1"></i>
                          {(selectedFile.size / 1024).toFixed(0)} KB
                        </Badge>
                      )}
                    </div>
                  </FormGroup>

                  {/* Preview */}
                  {previewUrl && (
                    <Card className="border mt-3">
                      <CardBody className="p-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted">معاينة</small>
                          <Button
                            color="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={clearSelection}
                          >
                            <i className="bx bx-x"></i>
                          </Button>
                        </div>
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="img-fluid rounded"
                          style={{ maxHeight: '150px', width: '100%', objectFit: 'cover' }}
                        />
                      </CardBody>
                    </Card>
                  )}

                  <Button
                    type="submit"
                    color="success"
                    block
                    size="lg"
                    className="mt-3"
                    disabled={!selectedFile || uploading}
                    style={{ borderRadius: '8px' }}
                  >
                    {uploading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        جاري الرفع...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-upload me-2"></i>
                        رفع البانر
                      </>
                    )}
                  </Button>
                </Form>
              </Col>

              {/* Gallery Section */}
              <Col lg={7}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <i className="bx bx-images me-2"></i>
                    البانرات المرفوعة
                  </h6>
                  <Badge color="primary">
                    {allBanners.length} بانر
                  </Badge>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2 text-muted">جاري التحميل...</p>
                  </div>
                ) : allBanners.length === 0 ? (
                  <Alert color="info" className="text-center">
                    <i className="bx bx-image-add font-size-24 d-block mb-2"></i>
                    لا توجد بانرات مرفوعة
                  </Alert>
                ) : (
                  <div
                    className="banners-grid"
                    style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      padding: '10px'
                    }}
                  >
                    <Row className="g-3">
                      {allBanners.map((banner, index) => (
                        <Col key={index} sm={6} md={4}>
                          <Card className="border shadow-sm position-relative overflow-hidden" style={{ borderRadius: '8px' }}>
                            <div
                              className="delete-overlay position-absolute top-0 end-0 m-2"
                              style={{ zIndex: 10 }}
                            >
                              <Button
                                color="danger"
                                size="sm"
                                className="btn-icon rounded-circle"
                                onClick={() => handleDeleteImage(index, banner)}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  padding: 0
                                }}
                              >
                                <i className="bx bx-trash font-size-16"></i>
                              </Button>
                            </div>
                            <CardImg
                              top
                              src={banner.url}
                              alt={`Banner ${index + 1}`}
                              style={{
                                height: '120px',
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(banner.url, '_blank')}
                            />
                            <div className="p-2 bg-light text-center">
                              <small className="text-muted">بانر #{index + 1}</small>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Alert color="warning" className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
          <i className="bx bx-error-circle me-2"></i>
          <strong>تنبيه:</strong> تأكد من أن الصور واضحة ومناسبة للعرض على الشاشات المختلفة.
        </Alert>
      </Col>

      <style>{`
        .upload-area.dragging {
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(85, 110, 230, 0.2);
        }
        
        .banners-grid::-webkit-scrollbar {
          width: 6px;
        }
        
        .banners-grid::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .banners-grid::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        
        .banners-grid::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .delete-overlay button {
          opacity: 0.9;
          transition: all 0.3s ease;
        }

        .delete-overlay button:hover {
          opacity: 1;
          transform: scale(1.1);
        }
      `}</style>
    </Row>
  );
}

export default BannerUpload;
